// The questionnaire pages' own words in one language, from GET
// /api/intake-link/ui. The last answer that arrived is held: a switch keeps
// its words until the new ones come, and keeps them for good if those never
// come - the form never blinks out or turns into the error card under the
// client.

import { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import type { IntakeLanguage, IntakeLinkUiResponse } from "@duralex/shared";
import { fetchIntakeUi } from "../api/intakeLink";

// The dead-link and failed-boot screens must speak even when nothing
// loaded at all.
const FALLBACK: Record<string, string> = {
  invalid_title: "This link is not valid",
  invalid_text: "It may have expired or been replaced. Contact the office for a new one.",
  error_title: "The page could not load",
  error_text: "Check your connection and try again in a minute.",
  error_retry: "Try again",
};

export function useIntakeWords(locale: IntakeLanguage, enabled = true) {
  const ui = useQuery({
    queryKey: ["intake-link", "ui", locale],
    queryFn: () => fetchIntakeUi(locale),
    enabled,
    staleTime: Infinity,
    retry: 1,
  });
  const [held, setHeld] = useState<IntakeLinkUiResponse | null>(null);
  useEffect(() => {
    if (ui.data) setHeld(ui.data);
  }, [ui.data]);
  const served = ui.data ?? held;
  const words = served?.ui;
  const t = (key: string): string => words?.[key] ?? FALLBACK[key] ?? key;
  // What a screen without questions is written in: the language the words
  // came in, which the server may have narrowed to English; English before
  // any arrive.
  const spokenIn: IntakeLanguage = served?.language ?? "en";
  return { words, t, spokenIn, isError: ui.isError, refetch: ui.refetch };
}
